import { useState, useEffect, useCallback } from 'react';
import { posApi } from '../../../api/pos';
import { toast } from '../../../components/toast';
import { openSunmiDrawer } from '../../../utils/sunmiPrinter';

interface PaidInOutModalProps {
  type: 'in' | 'out';
  onClose: () => void;
  onConfirm: (amount: number, reason: string, note: string) => void;
  userRole: 'cashier' | 'supervisor' | 'manager';
  currentBalance: number;
}

const cashInReasons: { label: string; value: number }[] = [
  { label: 'Change Refill', value: 0 },
  { label: 'Opening Float', value: 1 },
  { label: 'Bank Deposit Return', value: 2 },
  { label: 'Correction', value: 3 },
  { label: 'Other', value: 4 },
];

const cashOutReasons: { label: string; value: number }[] = [
  { label: 'Petty Cash', value: 0 },
  { label: 'Bank Deposit', value: 1 },
  { label: 'Supplier Payment', value: 2 },
  { label: 'Refund', value: 3 },
  { label: 'Correction', value: 4 },
  { label: 'Other', value: 5 },
];

export default function PaidInOutModal({ type, onClose, onConfirm, userRole, currentBalance }: PaidInOutModalProps) {
  const [step, setStep] = useState<'form' | 'pin'>('form');
  const [amount, setAmount] = useState('');
  const [selectedReason, setSelectedReason] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');

  const reasons = type === 'in' ? cashInReasons : cashOutReasons;
  const accentColor = type === 'in' ? 'green' : 'red';

  const getCounterSessionId = (): number | null => {
    try {
      const raw = localStorage.getItem('counterSession');
      const session = raw ? JSON.parse(raw) : null;
      return Number(
        session?.id ?? session?.counterSessionId ?? session?.sessionId ?? session?.counterSession?.id ?? 0
      ) || null;
    } catch {
      return null;
    }
  };

  const handleContinue = () => {
    const numAmount = parseFloat(amount);

    if (!amount || numAmount <= 0) {
      setError('Please enter a valid amount greater than 0');
      return;
    }
    if (selectedReason === null) {
      setError('Please select a reason');
      return;
    }
    if (userRole === 'cashier') {
      setError('Supervisor permission required for Paid In/Out operations');
      return;
    }
    if (type === 'out' && numAmount > currentBalance) {
      setError('Amount exceeds current drawer balance');
      return;
    }

    setError('');
    // Counter password disabled for now — may be revisited in the future.
    // setPin('');
    // setPinError('');
    // setStep('pin');
    handleVerifyPin('');
  };

  const handleVerifyPin = useCallback(async (_currentPin: string) => {
    // Counter password disabled for now — may be revisited in the future.
    // const counterId = (() => {
    //   try {
    //     const raw = localStorage.getItem('counter');
    //     return raw ? (Number(JSON.parse(raw)?.id) || 0) : 0;
    //   } catch { return 0; }
    // })();

    setIsLoading(true);
    setPinError('');
    // try {
    //   const verifyRes = await posApi.verifyPassword({
    //     counterId,
    //     password: Number(currentPin),
    //     passwordType: 1,
    //   });
    //   if (!verifyRes.succeeded || !verifyRes.data) {
    //     setPinError(verifyRes.message || 'Invalid PIN');
    //     setPin('');
    //     setIsLoading(false);
    //     return;
    //   }
    // } catch {
    //   setPinError('Failed to verify PIN. Please try again.');
    //   setPin('');
    //   setIsLoading(false);
    //   return;
    // }

    const counterSessionId = getCounterSessionId();
    if (!counterSessionId) {
      // Shown on the form now that the PIN step is skipped.
      setError('No active counter session found');
      setPinError('No active counter session found');
      setIsLoading(false);
      return;
    }
    try {
      const numAmount = parseFloat(amount);
      const payload = {
        counterSessionId,
        movementType: type === 'in' ? 0 : 1,
        amount: numAmount,
        reason: selectedReason!,
        note: note.trim() || '',
      };
      await posApi.createCashMovement(payload);
      openSunmiDrawer();
      toast.success(`${type === 'in' ? 'Paid In' : 'Paid Out'} recorded successfully`);
      const selectedLabel = reasons.find(r => r.value === selectedReason)?.label ?? String(selectedReason);
      onConfirm(numAmount, selectedLabel, note);
    } catch (err: any) {
      const msg = err?.message || 'Failed to record cash movement. Please try again.';
      toast.error(msg);
      setPinError(msg);
    } finally {
      setIsLoading(false);
    }
  }, [amount, type, selectedReason, note, reasons, onConfirm]);

  const handlePinInput = (digit: string) => {
    if (pin.length < 6) {
      setPin(prev => prev + digit);
      setPinError('');
    }
  };

  const handlePinDelete = () => {
    setPin(prev => prev.slice(0, -1));
    setPinError('');
  };

  const handlePinClear = () => {
    setPin('');
    setPinError('');
  };

  useEffect(() => {
    if (pin.length === 6) {
      handleVerifyPin(pin);
    }
  }, [pin, handleVerifyPin]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (step !== 'pin') return;
    if (e.key >= '0' && e.key <= '9') {
      handlePinInput(e.key);
    } else if (e.key === 'Backspace') {
      handlePinDelete();
    } else if (e.key === 'Escape') {
      handlePinClear();
    } else if (e.key === 'Enter' && pin.length === 6) {
      handleVerifyPin(pin);
    }
  }, [step, pin, handleVerifyPin]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const newBalance = type === 'in'
    ? currentBalance + (parseFloat(amount) || 0)
    : currentBalance - (parseFloat(amount) || 0);

  if (step === 'pin') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
        <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-2xl w-full sm:max-w-md flex flex-col max-h-[75vh] sm:h-[75vh] min-h-[300px] overflow-hidden">
          <div className={`${accentColor === 'green' ? 'bg-green-600' : 'bg-red-600'} text-white px-5 py-3 flex items-center justify-between rounded-t-2xl sm:rounded-t-xl`}>
            <div>
              <h2 className="text-xl font-bold whitespace-nowrap">Supervisor Authorization</h2>
              <p className="text-sm text-white/80 whitespace-nowrap">
                Enter PIN to confirm {type === 'in' ? 'Paid In' : 'Paid Out'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center hover:bg-white/20 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            <div className="mb-4 p-3 bg-gray-50 rounded-lg flex items-center gap-3">
              <i className={`${type === 'in' ? 'ri-arrow-down-circle-line text-green-600' : 'ri-arrow-up-circle-line text-red-600'} text-2xl`}></i>
              <div>
                <p className="text-xs text-gray-500">Confirming</p>
                <p className="text-sm font-semibold text-gray-900">
                  {type === 'in' ? 'Paid In' : 'Paid Out'} — {parseFloat(amount).toFixed(2)}
                </p>
              </div>
            </div>

            <div className="mb-3">
              <label className="block text-sm font-semibold text-gray-700 mb-2 whitespace-nowrap">Enter Supervisor PIN</label>
              <div className="flex items-center justify-center gap-2 mb-3">
                {[0, 1, 2, 3, 4, 5].map((index) => (
                  <div
                    key={index}
                    className={`w-11 h-11 flex items-center justify-center border-2 rounded-lg text-xl font-bold transition-all ${
                      pin.length > index
                        ? `border-${accentColor}-500 bg-${accentColor}-50 text-${accentColor}-600`
                        : 'border-gray-300 bg-white text-gray-400'
                    }`}
                  >
                    {pin.length > index ? '●' : '○'}
                  </div>
                ))}
              </div>

              {pinError && (
                <div className="p-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 mb-3">
                  <i className="ri-error-warning-line text-red-600 text-xl"></i>
                  <p className="text-sm text-red-800 whitespace-nowrap">{pinError}</p>
                </div>
              )}

              <div className="grid grid-cols-3 gap-2">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
                  <button
                    key={digit}
                    onClick={() => handlePinInput(digit.toString())}
                    className="h-11 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-lg text-xl font-bold transition-colors cursor-pointer"
                  >
                    {digit}
                  </button>
                ))}
                <button
                  onClick={handlePinClear}
                  className="h-11 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg text-sm font-bold transition-colors cursor-pointer whitespace-nowrap"
                >
                  Clear
                </button>
                <button
                  onClick={() => handlePinInput('0')}
                  className="h-11 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-lg text-xl font-bold transition-colors cursor-pointer"
                >
                  0
                </button>
                <button
                  onClick={handlePinDelete}
                  className="h-11 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition-colors cursor-pointer"
                >
                  <i className="ri-delete-back-2-line text-xl"></i>
                </button>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 px-4 py-3 bg-gray-50 flex items-center justify-between rounded-b-2xl sm:rounded-b-xl">
            <button
              onClick={() => { setStep('form'); setPin(''); setPinError(''); }}
              className="px-6 py-2.5 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-semibold whitespace-nowrap transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              Back
            </button>
            {isLoading && (
              <div className="flex items-center gap-2 text-gray-500 text-sm">
                <i className="ri-loader-4-line animate-spin"></i>
                Saving...
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full sm:max-w-md flex flex-col max-h-[75vh] sm:h-[75vh] min-h-[300px] overflow-hidden">
        {/* Sticky Header */}
        <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
          <div className="flex items-center gap-2">
            <i className={`${type === 'in' ? 'ri-arrow-down-circle-line text-green-600' : 'ri-arrow-up-circle-line text-red-600'} text-xl`}></i>
            <h2 className="text-lg font-semibold text-gray-900">
              {type === 'in' ? 'Paid In' : 'Paid Out'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl text-gray-600"></i>
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
          {userRole === 'cashier' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <i className="ri-error-warning-line text-red-500 text-lg mt-0.5"></i>
              <div>
                <p className="text-sm font-medium text-red-800">Supervisor Permission Required</p>
                <p className="text-xs text-red-600 mt-1">Cashiers cannot perform Paid In/Out operations.</p>
              </div>
            </div>
          )}

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600">Current Balance:</span>
              <span className="text-lg font-semibold text-gray-900">{currentBalance.toFixed(2)}</span>
            </div>
            {amount && parseFloat(amount) > 0 && (
              <div className="flex justify-between items-center pt-2 border-t border-gray-300">
                <span className="text-sm text-gray-600">New Balance:</span>
                <span className={`text-lg font-semibold ${newBalance < 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {newBalance.toFixed(2)}
                </span>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Amount <span className="text-red-500">*</span>
            </label>
            <input
              type="number" onWheel={(e) => e.currentTarget.blur()}
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                setError('');
              }}
              placeholder="0.00"
              step="0.01"
              min="0"
              autoFocus
              className="w-full px-3 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-2xl font-bold"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              {reasons.map((reason) => (
                <label
                  key={reason.value}
                  className={`flex items-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedReason === reason.value
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="reason"
                    value={reason.value}
                    checked={selectedReason === reason.value}
                    onChange={() => {
                      setSelectedReason(reason.value);
                      setError('');
                    }}
                    className="w-4 h-4 text-teal-600 cursor-pointer flex-shrink-0"
                  />
                  <span className="text-sm text-gray-700 leading-tight">{reason.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Note (Optional)
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Enter additional details..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none text-sm"
              rows={2}
              maxLength={200}
            />
            <p className="text-xs text-gray-500 mt-1">{note.length}/200 characters</p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
              <i className="ri-error-warning-line text-red-500"></i>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
        </div>

        {/* Sticky Footer */}
        <div className="flex gap-3 p-4 border-t bg-gray-50 flex-shrink-0 rounded-b-2xl sm:rounded-b-lg">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-100 font-medium text-gray-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={handleContinue}
            disabled={
              userRole === 'cashier' ||
              !amount ||
              parseFloat(amount) <= 0 ||
              selectedReason === null
            }
            className={`flex-1 px-4 py-2.5 ${type === 'in' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2`}
          >
            <i className="ri-shield-keyhole-line"></i>
            {`Confirm ${type === 'in' ? 'Paid In' : 'Paid Out'}`}
          </button>
        </div>
      </div>
    </div>
  );
}
